import { ArrowRight } from "lucide-react";
import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button, Card, Input } from "../components/ui";
import { slugify } from "../lib/format";

import { LANDING_URL } from "../lib/runtime-config";

/** Entrada de la app cuando no hay negocio en la URL. */
export default function Home(): ReactNode {
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  /*
   * El campo acepta lo que la persona tenga a mano — el nombre del negocio con
   * mayusculas, acentos y espacios — y se normaliza al identificador que viaja
   * en la URL. Antes se mandaba tal cual: "Mi Negocio" terminaba en
   * `/Mi Negocio`, que no existe, y con el campo vacio el boton no hacia
   * absolutamente nada, sin decir por que.
   */
  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const clean = slugify(slug);

    if (!clean) {
      setError("Escribi el identificador del negocio para poder buscarlo.");
      return;
    }

    setError(null);
    // `navigate` devuelve una promesa en React Router 7; aca no hay nada
    // que esperar, y `void` lo dice en lugar de dejarla suelta.
    void navigate(`/${clean}`);
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-[var(--border-subtle)]">
        <nav className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4" aria-label="Principal">
          <span className="font-semibold tracking-tight">TurnPro</span>
          {(
            <div className="flex items-center gap-2">
              <Link to="/ingresar" className="text-sm hover:underline">
                Ingresar
              </Link>
              <Link
                to="/registro"
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                Crear cuenta
              </Link>
            </div>
          )}
        </nav>
      </header>

      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 py-16">
        <h1 className="titulo text-3xl font-semibold tracking-tight">Reserva tu turno</h1>
        <p className="mt-2 text-muted">
          Ingresa el identificador del negocio para ver su pagina y elegir horario.
        </p>

        <Card className="mt-6 p-5">
          <form onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-3 sm:flex-row">
              <label htmlFor="slug" className="sr-only">
                Identificador del negocio
              </label>
              <Input
                id="slug"
                value={slug}
                onChange={(event) => {
                  setSlug(event.target.value);
                  if (error) setError(null);
                }}
                placeholder="mi-negocio"
                className="flex-1"
                autoComplete="off"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "slug-error" : undefined}
              />
              <Button type="submit" size="lg">
                Ver pagina
                <ArrowRight className="size-4" aria-hidden="true" />
              </Button>
            </div>
            {error ? (
              <p id="slug-error" role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            ) : null}
          </form>
        </Card>

        <p className="mt-8 text-sm text-muted">
          ¿Tenes un negocio y queres recibir reservas?{" "}
          <a href={LANDING_URL} className="text-brand-600 hover:underline">
            Conoce TurnPro
          </a>
        </p>
      </main>
    </div>
  );
}
